import React, { useState, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import i18n from "@/utils/i18n";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import DashboardLayout from "@/layouts/DashboardLayout";
import Header from "@/components/ui/header";
import Breadcrumb from "@/components/ui/breadcrumb";
import PageHeader from "@/components/ui/page-header";
import Table from "@/components/ui/table";
import Button from "@/components/ui/button";
import { LeftChevron, Download, Edit } from "@/components/icons";
import type { TableColumn, FilterField } from "@/types/component";
import { getAdminDepartments } from "@/lib/users.api";
import type { CertificateIssuedUser } from "../services/certificates.api";
import {
  certificatesApi,
  CertificateOwnerType,
} from "../services/certificates.api";
import { usePermission } from "@/core/permissions/hooks";
import { PERMISSIONS } from "@/core/permissions/config";
import { formatCertificateDate, generateAndDownloadCertificatePDFFromHtml } from "../utils/certificate-pdf-generator";
import styles from "./CertificateDetailPage.module.scss";

/** Detaydan gelen rawHtml'deki placeholder'ları Türkçe etiketlerle değiştirir (önizleme için) */
const RAW_HTML_PLACEHOLDER_LABELS: Record<string, string> = {
  "{{educationName}}": i18n.t("certificateDetail.tokens.educationName"),
  "{{participantName}}": i18n.t("certificateDetail.tokens.participantName"),
  "{{description}}": i18n.t("certificateDetail.tokens.description"),
  "{{instructorName}}": i18n.t("certificateDetail.tokens.instructorName"),
  "{{issueDate}}": i18n.t("certificateDetail.tokens.issueDate"),
  "{{certificateNumber}}": i18n.t("certificateDetail.tokens.certificateNumber"),
};

function formatRawHtmlWithLabels(rawHtml: string): string {
  let result = rawHtml;
  for (const [placeholder, label] of Object.entries(RAW_HTML_PLACEHOLDER_LABELS)) {
    result = result.split(placeholder).join(label);
  }
  return result;
}

const CertificateDetailPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { certificateId } = useParams<{ certificateId: string }>();

  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [departmentId, setDepartmentId] = useState<string | undefined>();
  const [, setFilterValues] = useState<Record<string, unknown>>({});
  const [users, setUsers] = useState<CertificateIssuedUser[]>([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [loading, setLoading] = useState(false);
  const [selectedUser, setSelectedUser] = useState<CertificateIssuedUser | null>(null);
  const [certificateDetail, setCertificateDetail] = useState<Awaited<ReturnType<typeof certificatesApi.getCertificateDetail>>>(null);
  const [detailLoading, setDetailLoading] = useState(true);
  const [departmentOptions, setDepartmentOptions] = useState<{ label: string; value: string }[]>([]);
  const [downloading, setDownloading] = useState(false);

  const certificateTitle = certificateDetail?.name ?? t("certificates.detail.title");

  useEffect(() => {
    getAdminDepartments()
      .then((list) => setDepartmentOptions(list.map((d) => ({ label: d.name, value: d.id }))))
      .catch(() => setDepartmentOptions([]));
  }, []);

  useEffect(() => {
    if (!certificateId) return;
    let isMounted = true;
    setDetailLoading(true);
    certificatesApi.getCertificateDetail(certificateId).then((detail) => {
      if (isMounted) {
        setCertificateDetail(detail);
        setDetailLoading(false);
      }
    }).catch(() => {
      if (isMounted) setDetailLoading(false);
    });
    return () => { isMounted = false; };
  }, [certificateId]);

  const fetchIssuedUsers = useCallback(async () => {
    if (!certificateId) return;
    setLoading(true);
    try {
      const { data, total } = await certificatesApi.getCertificateIssuedUsers(
        certificateId,
        {
          page: currentPage,
          limit: pageSize,
          departmentId,
          search: searchTerm.trim() || undefined,
        }
      );
      setUsers(data);
      setTotalRecords(total);
    } catch {
      setUsers([]);
      setTotalRecords(0);
    } finally {
      setLoading(false);
    }
  }, [certificateId, currentPage, pageSize, departmentId, searchTerm]);

  useEffect(() => {
    fetchIssuedUsers();
  }, [fetchIssuedUsers]);

  const { hasPermission } = usePermission();
  const canUpdate = hasPermission(PERMISSIONS.CERTIFICATES_UPDATE);

  const handleGoBack = () => {
    navigate("/dashboard/certificates");
  };

  const breadcrumbItems = [
    {
      label: t("certificates.title"),
      onClick: () => navigate("/dashboard/certificates"),
    },
    {
      label: certificateTitle,
    },
  ];

  const filterFields: FilterField[] = [
    {
      name: "departmentId",
      label: t("certificates.detail.filters.department"),
      type: "dropdown",
      placeholder: t("certificates.detail.filters.departmentPlaceholder"),
      options: departmentOptions,
    },
  ];

  const handleFilterChange = (filters: Record<string, unknown>) => {
    setFilterValues(filters);
    setDepartmentId(filters.departmentId as string | undefined);
    setCurrentPage(1);
  };

  const handleRowClick = (row: CertificateIssuedUser) => {
    setSelectedUser(row);
  };

  const htmlToDownload = selectedUser?.parsedHtml ?? (certificateDetail?.rawHtml ? formatRawHtmlWithLabels(certificateDetail.rawHtml) : null);
  const handleDownload = async () => {
    if (!htmlToDownload?.trim()) {
      toast.error(t("certificates.detail.downloadSelectUser"));
      return;
    }
    setDownloading(true);
    try {
      const name = selectedUser ? `${selectedUser.ad} ${selectedUser.soyad}`.trim() : certificateTitle;
      const filename = `sertifika-${name.replace(/\s+/g, "-")}-${Date.now()}`;
      await generateAndDownloadCertificatePDFFromHtml(htmlToDownload, filename);
      toast.success(t("certificates.download.success"));
    } catch {
      toast.error(t("certificates.download.error"));
    } finally {
      setDownloading(false);
    }
  };

  const columns: TableColumn<CertificateIssuedUser>[] = [
    {
      header: t("certificates.detail.columns.personnelName"),
      accessor: (row) => `${row.ad} ${row.soyad}`.trim() || "-",
    },
    {
      header: t("certificates.detail.columns.title"),
      accessor: (row) => row.title || "-",
    },
    {
      header: t("certificates.detail.columns.department"),
      accessor: (row) => row.department?.name ?? "-",
    },
  ];

  return (
    <DashboardLayout>
      <Header title={certificateTitle} />

      <div className={styles.container}>
        <div className={styles.breadcrumbContainer}>
          <button
            type="button"
            className={styles.backButton}
            onClick={handleGoBack}
            aria-label={t("common.back")}
          >
            <LeftChevron width={20} height={20} />
          </button>
          <Breadcrumb items={breadcrumbItems} />
        </div>

        <div className={styles.pageTitleRow}>
          <h1 className={styles.pageTitle}>{certificateTitle}</h1>
          {certificateDetail?.ownerType !== undefined && (
            <span className={styles.ownerTypeBadge}>
              {certificateDetail.ownerType === CertificateOwnerType.INTERN
                ? t("certificates.ownerType.intern")
                : t("certificates.ownerType.education")}
            </span>
          )}
          {canUpdate && certificateId && (
            <Button
              variant="outlined"
              color="primary"
              size="medium"
              startIcon={<Edit width={18} height={18} />}
              onClick={() =>
                navigate(`/dashboard/certificates/${certificateId}/edit`)
              }
              className={styles.editButton}
            >
              {t("certificates.card.edit")}
            </Button>
          )}
        </div>

        <div className={styles.pageLayout}>
          {/* Left Column - Certificate (parsedHtml of selected user) + detay */}
          <div className={styles.leftColumn}>
            <div className={styles.certificateCard}>
              <div className={styles.certificatePreview}>
                {selectedUser?.parsedHtml ? (
                  <div
                    className={styles.certificateRawHtml}
                    dangerouslySetInnerHTML={{ __html: selectedUser.parsedHtml }}
                  />
                ) : certificateDetail?.rawHtml ? (
                  <div
                    className={styles.certificateRawHtml}
                    dangerouslySetInnerHTML={{
                      __html: formatRawHtmlWithLabels(certificateDetail.rawHtml),
                    }}
                  />
                ) : (
                  <div className={styles.certificatePlaceholder}>
                    {t("certificates.detail.selectUser")}
                  </div>
                )}
              </div>
              {!detailLoading && certificateDetail && (
                <>
                <div className={styles.downloadButtonWrap}>
                  <Button
                    variant="outlined"
                    color="primary"
                    size="medium"
                    startIcon={<Download width={18} height={18} />}
                    onClick={handleDownload}
                    disabled={!htmlToDownload?.trim() || downloading}
                  >
                    {downloading ? t("common.loading") : t("common.download")}
                  </Button>
                </div>
                <div className={styles.certificateDetails}>
                  <div className={styles.detailItem}>
                    <span className={styles.detailLabel}>
                      {t("certificates.detail.relatedTraining")}
                    </span>
                    <span className={styles.detailValue}>
                      {/* education: many-to-many, array. Birden fazla varsa
                          virgülle birleştir; yoksa tire göster. */}
                      {certificateDetail.education && certificateDetail.education.length > 0
                        ? certificateDetail.education.map((e) => e.name).join(", ")
                        : "-"}
                    </span>
                  </div>
                  <div className={styles.detailItem}>
                    <span className={styles.detailLabel}>
                      {t("certificates.detail.assignedCount")}
                    </span>
                    <span className={styles.detailValue}>
                      {certificateDetail.issuedCount ?? 0} {t("certificates.detail.people")}
                    </span>
                  </div>
                  <div className={styles.detailItem}>
                    <span className={styles.detailLabel}>
                      {t("certificates.detail.createdDate")}
                    </span>
                    <span className={styles.detailValue}>
                      {certificateDetail.createdAt
                        ? formatCertificateDate(certificateDetail.createdAt)
                        : "-"}
                    </span>
                  </div>
                </div>
                </>
              )}
            </div>
          </div>

          {/* Right Column - Issued users table */}
          <div className={styles.rightColumn}>
            <PageHeader
              searchPlaceholder={t("certificates.detail.searchPlaceholder")}
              searchValue={searchTerm}
              onSearch={(value) => {
                setSearchTerm(value);
                setCurrentPage(1);
              }}
              filters={filterFields}
              onFilterChange={handleFilterChange}
              className={styles.pageHeader}
            />

            <div className={styles.tableSection}>
              <Table<CertificateIssuedUser>
                columns={columns}
                data={users}
                getRowId={(row) => row.id}
                isLoading={loading}
                totalRecords={totalRecords}
                pageSize={pageSize}
                currentPage={currentPage}
                onPageChange={setCurrentPage}
                onPageSizeChange={(size) => {
                  setPageSize(size);
                  setCurrentPage(1);
                }}
                emptyMessage={t("certificates.detail.noPersonnel")}
                hasVisibleActions={false}
                onRowClick={handleRowClick}
              />
            </div>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default CertificateDetailPage;
